/**
 * PriceExplanationsSection — admin-only panel of Phyllo's pricing
 * rationale. 2-col card grid mirroring the PricingSection visual
 * rhythm. Renders only populated keys (Phyllo returns null for
 * non-applicable rationales like 'shop' on non-shop creators).
 */
import { useMemo } from 'react';
import type {
  InsightIQPriceExplanations,
  InsightIQPriceExplanation,
} from '@discovr/types';
import { SectionTitle, Badge } from '../charts/StatCard';
import { levelToBadgeType } from '../utils/formatters';

interface PriceExplanationsSectionProps {
  explanations: InsightIQPriceExplanations;
}

const KEY_LABELS: ReadonlyArray<readonly [keyof InsightIQPriceExplanations, string]> = [
  ['engagement', 'Engagement'],
  ['follower_level', 'Follower Level'],
  ['audience_location', 'Audience Location'],
  ['audience_credibility', 'Audience Credibility'],
  ['views', 'Views'],
  ['content_type', 'Content Type'],
  ['category', 'Category'],
  ['shop', 'Shop'],
] as const;

export function PriceExplanationsSection({ explanations }: PriceExplanationsSectionProps) {
  const populated = useMemo(() => {
    return KEY_LABELS
      .map(([key, label]) => {
        const entry = explanations[key];
        return entry ? ([label, entry] as const) : null;
      })
      .filter((x): x is readonly [string, InsightIQPriceExplanation] => x !== null);
  }, [explanations]);

  if (populated.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>Price Explanations</SectionTitle>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {populated.map(([label, entry]) => (
          <ExplanationCard key={label} label={label} entry={entry} />
        ))}
      </div>
    </div>
  );
}

function ExplanationCard({ label, entry }: { label: string; entry: InsightIQPriceExplanation }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2.5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <Badge label={entry.level} type={levelToBadgeType(entry.level)} />
      </div>
      <div className="text-caption-regular text-muted-foreground">{entry.description}</div>
    </div>
  );
}
